import React, {useEffect, useMemo, useRef, useState} from "react";
import ChecklistProgress, {ChecklistSkeleton} from "./checklist-progress";
import DropZone from "../dropzone/drop-zone-alt";
import ExclamationTriangleIcon from "@heroicons/react/24/outline/ExclamationTriangleIcon";
import {classNames} from "../../util/util-helpers";
import Button from "../button";
import Tooltip from "../tooltip";
import DocBadge from "../badge/doc-badge";
import {
    CHECKLIST_HISTORY_TYPE_DOCUMENT,
    CHECKLIST_HISTORY_TYPE_NOTE,
    CHECKLIST_HISTORY_TYPE_REMINDER,
    CHECKLIST_STEP_STATUS_COMPLETED,
    CHECKLIST_STEP_STATUS_OPEN
} from "../../util/util-consts";
import ClockIcon from "@heroicons/react/24/outline/ClockIcon";
import CheckIcon from "@heroicons/react/24/outline/CheckIcon";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import PhoneIcon from "@heroicons/react/24/outline/PhoneIcon";
import BellIcon from "@heroicons/react/24/outline/BellIcon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import {toFrontDateFromUTC, toFrontDateTimeFromUTC} from "../../util/util-dates";
import ButtonIcon from "../button/button-icon";
import SlideOver from "../slideover";
import {LoaderSmall} from "../loader";

const STATUS_NOT_STARTED = 1;
const STATUS_WAITING = 2;
const STATUS_COMPLETED = 3;

function formatChecklistDate(dateStr, isAllDay = false) {
    if (!dateStr) return null;
    if (isAllDay) return toFrontDateFromUTC(dateStr) || dateStr;
    return toFrontDateTimeFromUTC(dateStr) || dateStr;
}

const HISTORY_ICONS = {
    1: DocumentTextIcon,
    2: PencilIcon,
    3: CheckIcon,
    4: PhoneIcon,
    5: BellIcon,
};

const HistoryIcon = ({typeID}) => {
    const Icon = HISTORY_ICONS[typeID] ?? ClockIcon;
    const colorClass = HISTORY_ICONS[typeID] ? "text-emerald-500" : "text-blue-500";
    return <Icon className={classNames("w-4.5 h-4.5 mt-px shrink-0", colorClass)}/>;
};

function StatusIcon({status}) {
    if (status === "completed") {
        return (
            <span className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-inverse">
                <CheckIcon className="w-3.5 h-3.5" strokeWidth={3}/>
            </span>
        );
    }
    if (status === "waiting") {
        return (
            <ExclamationTriangleIcon className="size-6 bg-amber-400 rounded-full text-inverse p-1"/>
        );
    }
    return (
        <span className="w-6 h-6 rounded-full bg-tm-gray-200 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-tm-gray-400" fill="none" stroke="currentColor" strokeWidth="2"
                 viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
            </svg>
        </span>
    );
}

function ChecklistItem({
                           item,
                           actions,
                           defaultOpen,
                           translate,
                           isLoading,
                           isUploadLoading,
                           isBlocked,
                           isReadOnly,
                           canToggleSubStep = true,
                           onSaveNote,
                           onToggleSubStep,
                           onStepAction,
                           RequiredDocuments,
                           Documents = [],
                           onFilesDrop,
                           accept,
                           onPreviewDocument,
                       }) {
    const {
        StepName,
        StatusID,
        Notes,
        History,
        SubSteps,
        CompletedDate,
        AllowIncomplete,
        AutoComplete
    } = item;

    const isCompleted = StatusID === STATUS_COMPLETED;
    const isWaiting = StatusID === STATUS_WAITING;
    const isNotStarted = StatusID === STATUS_NOT_STARTED;

    const hasExpandable = true //!!(Notes || History?.length || RequiredDocuments?.length || SubSteps?.length);
    const [isOpen, setIsOpen] = useState(defaultOpen && hasExpandable);
    const [subStepStates, setSubStepStates] = useState(() => (SubSteps ?? []).map(s => s.Completed));
    const isCompleteDisabled = !!(SubSteps?.length && !AllowIncomplete && !subStepStates.every(Boolean));
    const [stepNote, setStepNote] = useState(Notes ?? "");
    const [isNotesFocused, setIsNotesFocused] = useState(false);
    const [isDocumentDragged, setIsDocumentDragged] = useState(false);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const pendingDocumentTypeIDRef = useRef(null);
    const isAnyLoading = isLoading || isUploadLoading;

    const handleToggleSubStep = (idx) => {
        const newCompleted = !subStepStates[idx];

        setSubStepStates(prev => prev.map((v, i) => i === idx ? newCompleted : v));

        onToggleSubStep(idx, newCompleted, SubSteps[idx].ChecklistSubStepID);
    };

    const handleSaveNote = () => {
        onSaveNote(stepNote);
    };

    const handleStepActionButtonClick = (StepID, StatusID) => {
        onStepAction(StepID, StatusID);
    };

    const prevHistoryIdsRef = useRef(null);

    const newHistoryIds = useMemo(() => {
        if (prevHistoryIdsRef.current === null || !History) return new Set();
        return new Set(
            History
                .filter(e => !prevHistoryIdsRef.current.has(e.ChecklistStepHistoryID))
                .map(e => e.ChecklistStepHistoryID)
        );
    }, [History]);

    const handleDocumentDragEnter = () => setIsDocumentDragged(true);
    const handleDocumentDragStop = () => setIsDocumentDragged(false);

    const statusKey = isCompleted ? "completed" : isWaiting ? "waiting" : "not-started";
    const formattedCompletedDate = formatChecklistDate(CompletedDate);

    useEffect(() => {
        if (History) {
            prevHistoryIdsRef.current = new Set(History.map(e => e.ChecklistStepHistoryID));
        }
    }, [History]);

    useEffect(() => {
        if (textareaRef.current && stepNote) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
        }
    }, []);

    useEffect(() => {
        if (!onFilesDrop) return;
        const handleMouseUp = () => setIsDocumentDragged(false);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("dragend", handleMouseUp);
        document.addEventListener("drop", handleMouseUp);
        document.addEventListener("mouseleave", handleMouseUp);
        return () => {
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("dragend", handleMouseUp);
            document.removeEventListener("drop", handleMouseUp);
            document.removeEventListener("mouseleave", handleMouseUp);
        };
    }, [onFilesDrop]);

    return (
        <div className="relative"
             onDragEnter={onFilesDrop && isOpen && !isBlocked ? handleDocumentDragEnter : undefined}>
            {/* Header row — always visible */}
            <div
                className={classNames("flex items-center justify-between px-6 py-4 border-b", isWaiting ? "border-amber-200" : "border-tm-gray-100", hasExpandable && "cursor-pointer hover:bg-tm-gray-50 rounded-t-2xl", hasExpandable && !isOpen && "rounded-b-2xl")}
                onClick={hasExpandable ? () => setIsOpen(o => !o) : undefined}
            >
                {/* Left side */}
                <div className="flex items-center gap-3">
                    <StatusIcon status={statusKey}/>

                    <span className="text-tm-gray-800 font-medium text-sm">{StepName}</span>

                    {item.CanSkipStep === 1 && (
                        <Tooltip content="Optional: this step can be skipped.">
                            <span
                                className="text-xs font-medium bg-tm-gray-100 text-tm-gray-500 px-2 py-0.5 rounded-full">Optional</span>
                        </Tooltip>
                    )}

                    {AutoComplete === 1 && (
                        <Tooltip content="Automatically completes when all substeps are done.">
                            <span
                                className="text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Autocomplete</span>
                        </Tooltip>
                    )}
                </div>

                {/* Right side */}
                <div className="flex items-center gap-2 shrink-0">
                    {formattedCompletedDate && (
                        <span className="text-xs text-tm-gray-400">{formattedCompletedDate}</span>
                    )}

                    {isCompleted && (
                        <span
                            className="text-xs font-bold bg-emerald-600/10 text-emerald-600 px-2.5 py-0.5 rounded-full">{translate("text.completed")}</span>
                    )}

                    {isWaiting && (
                        <>
                            <span
                                className="text-xs font-bold bg-amber-600/10 text-amber-600 px-2.5 py-0.5 rounded-full">{translate("text.waiting")}</span>

                            {/*{!formattedCompletedDate && (*/}
                            {/*    <button*/}
                            {/*        onClick={e => e.stopPropagation()}*/}
                            {/*        className="bg-primary hover:bg-primary-shade text-inverse text-xs font-medium px-4 py-1.5 rounded-btn transition"*/}
                            {/*    >*/}
                            {/*        {translate("btn.complete")}*/}
                            {/*    </button>*/}
                            {/*)}*/}
                        </>
                    )}

                    {isNotStarted && (
                        <span
                            className="text-xs font-bold text-tm-gray-400 border border-tm-gray-600/10 px-2.5 py-0.5 rounded-full">{translate("text.not_started")}</span>
                    )}

                    {hasExpandable && (
                        <ChevronDownIcon className={classNames(
                            "w-4 h-4 text-tm-gray-400 transition-transform duration-200",
                            isOpen && "rotate-180"
                        )}/>
                    )}
                </div>
            </div>

            {/* Collapsible notes + history */}
            {!!onFilesDrop && isOpen && !isBlocked && (
                <React.Fragment>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        multiple
                        accept={accept}
                        onChange={(e) => {
                            const files = Array.from(e.target.files);
                            e.target.value = '';
                            const preselectedDocTypeID = pendingDocumentTypeIDRef.current;
                            pendingDocumentTypeIDRef.current = null;
                            if (files.length) onFilesDrop(files, item, preselectedDocTypeID);
                        }}
                    />

                    <DropZone
                        className={classNames(
                            "absolute mb-0",
                            isDocumentDragged ? "inset-0 z-50" : "left-0 right-0 bottom-0"
                        )}
                        maxFilesAccepted={10}
                        accept={accept}
                        onDragLeave={handleDocumentDragStop}
                        onAcceptFiles={(acceptedFiles) => {
                            setIsDocumentDragged(false);
                            onFilesDrop(acceptedFiles, item);
                        }}
                        content={(isDragAccept, isDragReject) => (
                            <div
                                className={classNames(
                                    isDocumentDragged && isDragAccept ? "border-2 border-dashed border-primary bg-primary-transparent" : undefined,
                                    isDocumentDragged && isDragReject ? "border-2 border-dashed border-red-600 bg-red-600/10" : undefined,
                                    !isDragAccept && !isDragReject ? "border-transparent hidden" : undefined,
                                    "pb-4 absolute inset-0 rounded-2xl"
                                )}
                                onDragLeave={handleDocumentDragStop}
                            />
                        )}
                    />
                </React.Fragment>
            )}

            {hasExpandable && isOpen && (
                <div className="pt-3 pb-4 pl-9 space-y-3 pr-6">
                    {/* Action buttons row — visible when expanded */}
                    {!isReadOnly && actions && actions.length > 0 && (
                        <div
                            className="flex flex-wrap gap-1.5"
                            onClick={e => e.stopPropagation()}
                        >
                            {actions.map((action, idx) => {
                                const isDisabled = isBlocked || action.hasPerm === false;
                                return (
                                    <Tooltip
                                        key={action.key ?? idx}
                                        disabled={!isDisabled}
                                        content={
                                            isBlocked
                                                ? translate("text.complete_required_previous_steps")
                                                : translate("text.no_permission")
                                        }
                                    >
                                        <span>
                                            <button
                                                type="button"
                                                disabled={isDisabled}
                                                onClick={() => {
                                                    action.onClick(item);
                                                    if (action.openFileDialog && onFilesDrop) {
                                                        fileInputRef.current?.click();
                                                    }
                                                }}
                                                className={classNames(
                                                    "relative inline-flex items-center gap-2 rounded-full bg-tm-gray-50 px-2 py-1.5 text-xs font-medium whitespace-nowrap text-tm-gray-600 transition-colors sm:px-3",
                                                    isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-tm-gray-100 cursor-pointer"
                                                )}
                                            >
                                                {action.icon}

                                                <span>{action.label}</span>
                                            </button>
                                        </span>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    )}


                    <div className="relative mb-3.5">
                        <textarea
                            ref={textareaRef}
                            className={classNames(
                                "w-full pl-3 pr-20 py-2.5 text-sm text-tm-gray-700 border-0 border-l-2 resize-none outline-none ring-0 focus:ring-0 placeholder:text-tm-gray-400 transition-colors overflow-hidden",
                                isBlocked || isReadOnly ? "opacity-50 cursor-not-allowed" : "",
                                isNotesFocused ? "bg-primary/10 border-primary" : "bg-tm-gray-100 border-tm-gray-300"
                            )}
                            disabled={isBlocked || isReadOnly}
                            placeholder={isBlocked ? translate("text.complete_required_previous_steps") : "No notes yet. Type here to add one."}
                            value={stepNote}
                            rows={1}
                            onChange={e => {
                                setStepNote(e.target.value);
                                const el = e.target;
                                el.style.height = "auto";
                                el.style.height = el.scrollHeight + "px";
                            }}
                            onFocus={() => setIsNotesFocused(true)}
                            onBlur={() => setIsNotesFocused(false)}
                            onKeyDown={e => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveNote();
                                }
                            }}
                            onClick={e => e.stopPropagation()}
                        />

                        {(isLoading || stepNote !== (Notes ?? "")) && (
                            <div className="absolute bottom-2 right-2">
                                {isLoading ? (
                                    <LoaderSmall disableContainer={true}/>
                                ) : (
                                    <button
                                        className="cursor-pointer rounded-btn bg-primary px-2 py-1 text-xs font-medium text-white hover:bg-primary-shade disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={isBlocked || isReadOnly}
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleSaveNote();
                                        }}
                                    >
                                        Save
                                    </button>
                                )}
                            </div>
                        )}
                    </div>


                    {SubSteps && SubSteps.length > 0 && (() => {
                        const subStepTotal = SubSteps.length;
                        const subStepCompleted = subStepStates.filter(Boolean).length;
                        const subStepProgress = subStepTotal > 0 ? Math.round((subStepCompleted / subStepTotal) * 100) : 0;
                        return (
                            <div className="mb-3.5">
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-medium text-tm-gray-400 uppercase tracking-wide">Sub-steps</p>

                                    <span
                                        className={classNames("text-xs font-medium", subStepCompleted === subStepTotal ? "text-emerald-600" : "text-tm-gray-500")}>{subStepCompleted} of {subStepTotal} done</span>
                                </div>

                                <div className="relative w-full h-1 bg-tm-gray-100 rounded-full mb-3 overflow-hidden">
                                    <div
                                        className="absolute inset-y-0 left-0 h-full bg-emerald-500 rounded-full transition-all duration-500"
                                        style={{width: `${subStepProgress}%`}}/>
                                </div>

                                <div className="flex flex-col divide-y divide-tm-gray-100">
                                    {SubSteps.map((subStep, idx) => {
                                        const isDone = subStepStates[idx];
                                        const {subStepReminder, isReminderOverdue} = (() => {
                                            const toMs = (d) => new Date(d?.replace(' ', 'T') + 'Z').getTime();
                                            const reminders = (History ?? []).filter(e =>
                                                e.TypeID === CHECKLIST_HISTORY_TYPE_REMINDER &&
                                                e.ReminderDate !== null &&
                                                e.ChecklistSubStepID === subStep.ChecklistSubStepID &&
                                                !e.IsCompleted &&
                                                !e.IsArchived &&
                                                !e.IsDone
                                            );
                                            if (!reminders.length) return {
                                                subStepReminder: null,
                                                isReminderOverdue: false
                                            };
                                            const now = Date.now();
                                            const overdue = reminders.filter(e => toMs(e.ReminderDate) < now);
                                            const pool = overdue.length ? overdue : reminders;
                                            const selected = pool.reduce((a, b) => toMs(a.ReminderDate) < toMs(b.ReminderDate) ? a : b);
                                            return {subStepReminder: selected, isReminderOverdue: overdue.length > 0};
                                        })();
                                        const isSubStepDisabled = isReadOnly || !canToggleSubStep;
                                        return (
                                            <div
                                                key={subStep.ChecklistSubStepID ?? idx}
                                                className="flex flex-wrap items-start gap-2.5 py-2"
                                            >
                                                <Tooltip
                                                    disabled={canToggleSubStep}
                                                    content={translate("text.no_permission")}
                                                >
                                                    <span>
                                                        <button className={classNames(
                                                            "size-4 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5",
                                                            isDone ? "border-emerald-500 bg-emerald-500" : "border-tm-gray-300 bg-inverse",
                                                            isSubStepDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                                                        )}
                                                                disabled={isSubStepDisabled}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    if (!isSubStepDisabled) handleToggleSubStep(idx);
                                                                }}
                                                        >
                                                            {!!isDone && (
                                                                <CheckIcon className="w-2.5 h-2.5 text-inverse"
                                                                           strokeWidth={3}/>
                                                            )}
                                                        </button>
                                                    </span>
                                                </Tooltip>

                                                <div className="flex-1">
                                                    <p className={classNames("text-xs font-bold", isDone ? "text-tm-gray-400 line-through" : "text-tm-gray-700")}>
                                                        {subStep.Label}

                                                        {!!subStepReminder && (
                                                            <span className={classNames(
                                                                "inline-flex items-center gap-1 font-normal px-1.5 py-0.5 rounded-full ml-1.5",
                                                                isReminderOverdue
                                                                    ? "text-red-600 bg-red-50"
                                                                    : "text-primary bg-primary-transparent"
                                                            )}>
                                                                <BellIcon className="w-3 h-3 shrink-0"/>
                                                                {formatChecklistDate(subStepReminder.ReminderDate, subStepReminder.IsAllDay)}
                                                            </span>
                                                        )}
                                                    </p>

                                                    <p className="text-xs text-tm-gray-500 mt-0.5">{
                                                        isDone
                                                            ? (subStep?.CompletedByContact ? <span
                                                                className="italic">{(formatChecklistDate(subStep.CompletedDate) ?? "Pending")} {translate("text.checked_by", [subStep.CompletedByContact])}</span> : (formatChecklistDate(subStep.CompletedDate) ?? "Pending"))
                                                            : subStep.CompletedDate && subStep.CompletedByContact ?
                                                                <span
                                                                    className="italic">{formatChecklistDate(subStep.CompletedDate)} {translate("text.unchecked_by", [subStep.CompletedByContact])}</span> : "Pending"}
                                                    </p>

                                                    {(() => {
                                                        const ids = subStep.RequiredDocumentTypeIDs ? JSON.parse(subStep.RequiredDocumentTypeIDs) : [];
                                                        const docs = (RequiredDocuments ?? []).filter(d => ids.includes(String(d.DocumentTypeID)));

                                                        if (!docs.length) return null;

                                                        return (
                                                            <div
                                                                className="flex flex-wrap items-center gap-2 text-sm mt-2">
                                                                {docs.map((doc, docIdx) => (
                                                                    <DocBadge
                                                                        key={doc.DocumentTypeID ?? docIdx}
                                                                        label={doc.DocumentType}
                                                                        status={doc.isUploaded ? "uploaded" : "required"}
                                                                        onClick={!doc.isUploaded && onFilesDrop && !isReadOnly && !isBlocked ? (e) => {
                                                                            e.stopPropagation();
                                                                            pendingDocumentTypeIDRef.current = doc.DocumentTypeID;
                                                                            fileInputRef.current?.click();
                                                                        } : undefined}
                                                                    />
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}

                    {History && History.length > 0 && (
                        <div className="space-y-2 text-sm text-tm-gray-500">
                            {History.filter(e => !(e.TypeID === CHECKLIST_HISTORY_TYPE_REMINDER && e.ReminderDate === null)).map((entry, idx) => (

                                <div
                                    key={entry.ChecklistStepHistoryID ?? idx}
                                    className={classNames(
                                        "flex items-start gap-2 border-l-2 pl-2 rounded-btn",
                                        !HISTORY_ICONS[entry.TypeID] ? "border-blue-400" : "border-emerald-400",
                                        newHistoryIds.has(entry.ChecklistStepHistoryID) ? "animate-[fadeIn_0.4s_ease-out_forwards]" : undefined
                                    )}
                                >
                                    <HistoryIcon typeID={entry.TypeID}/>

                                    {entry.TypeID === CHECKLIST_HISTORY_TYPE_REMINDER ? (
                                        <div>
                                            <p>
                                                <span
                                                    className="text-tm-gray-600 font-medium">{formatChecklistDate(entry.CreatedDate)}</span>

                                                {" "}

                                                <span className="italic text-tm-gray-700">
                                                    {"Reminder set for "}
                                                    <span
                                                        className="not-italic font-medium text-primary">{formatChecklistDate(entry.ReminderDate, entry.IsAllDay)}</span>
                                                    {entry.Contact && <>{" by "}{entry.Contact}</>}
                                                </span>
                                            </p>

                                            {!!entry.ReminderNotes && (
                                                <p className="text-xs text-tm-gray-500 mt-0.5">{entry.ReminderNotes}</p>
                                            )}
                                        </div>
                                    ) : entry.TypeID === CHECKLIST_HISTORY_TYPE_NOTE ? (
                                        <div>
                                            <p>
                                                <span className="text-tm-gray-600 font-medium">{formatChecklistDate(entry.CreatedDate)}</span>

                                                {" "}

                                                {"Comment by "}{entry.Contact}
                                            </p>

                                            {!!entry.Text && (
                                                <p className="text-xs text-tm-gray-500 mt-0.5">{entry.Text}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <span>
                                            <span
                                                className="text-tm-gray-600 font-medium">{formatChecklistDate(entry.CreatedDate)}</span>
                                            &nbsp;
                                            <span
                                                className={classNames("italic", HISTORY_ICONS[entry.TypeID] ? "text-tm-gray-700" : undefined)}>
                                                {!!entry.DocumentID && (() => {
                                                    const doc = Documents.find(d => d.DocumentID === entry.DocumentID);
                                                    if (!doc) return null;
                                                    return (
                                                        <span
                                                            className={classNames(
                                                                "not-italic font-medium ml-1",
                                                                onPreviewDocument ? "cursor-pointer underline text-primary hover:text-primary-tint" : undefined
                                                            )}
                                                            onClick={onPreviewDocument ? () => onPreviewDocument(doc) : undefined}
                                                        >
                                                            {doc.DocumentType}
                                                            {" "}
                                                        </span>
                                                    );
                                                })()}

                                                {(!entry.TypeID || entry.TypeID === CHECKLIST_HISTORY_TYPE_DOCUMENT)
                                                    ? translate("text." + entry.Text, [entry.Contact])
                                                    : entry.Text}
                                            </span>

                                            {entry.Link && (
                                                <>
                                                    {" "}
                                                    <span className="text-primary underline cursor-pointer">
                                                        {entry.Link}
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        {!isReadOnly && isNotStarted && (
                            <Tooltip
                                disabled={!isBlocked}
                                content={isBlocked ? translate("text.complete_required_previous_steps") : null}
                            >
                                <span>
                                    <Button
                                        className="flex items-center gap-1 bg-primary hover:bg-primary-shade text-inverse text-xs font-bold px-4 py-0 h-7 rounded-btn transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        isLoading={isAnyLoading}
                                        disabled={isAnyLoading || isBlocked}
                                        loaderClass="text-white"
                                        onClick={() => handleStepActionButtonClick(item?.ChecklistStepID, CHECKLIST_STEP_STATUS_OPEN)}
                                    >
                                        {translate("btn.open")}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}

                        {!isReadOnly && isWaiting && (
                            <Tooltip
                                disabled={!isCompleteDisabled}
                                content={translate("text.complete_all_substeps_first")}
                            >
                                <span>
                                    <Button
                                        className={
                                            classNames(
                                                "flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-inverse text-xs font-bold px-4 py-0 h-7 rounded-btn transition",
                                                isAnyLoading || isCompleteDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                                            )
                                        }
                                        isLoading={isAnyLoading}
                                        disabled={isAnyLoading || isCompleteDisabled}
                                        loaderClass="text-white"
                                        onClick={() => handleStepActionButtonClick(item?.ChecklistStepID, CHECKLIST_STEP_STATUS_COMPLETED)}
                                    >
                                        {translate("btn.complete")}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}

                        {!isReadOnly && isCompleted && (
                            <Button
                                className="flex items-center gap-1 bg-amber-400 hover:bg-amber-500 text-inverse text-xs font-bold px-4 py-0 h-7 rounded-btn transition"
                                isLoading={isAnyLoading}
                                disabled={isAnyLoading}
                                loaderClass="text-white"
                                onClick={() => handleStepActionButtonClick(item?.ChecklistStepID, CHECKLIST_STEP_STATUS_OPEN)}
                            >
                                {translate("btn.reopen")}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export {ChecklistSkeleton};

export default function Checklist({
                                      items = [],
                                      actions,
                                      translate,
                                      title,
                                      isLoading,
                                      notFound = false,
                                      onSaveNote,
                                      onToggleSubStep,
                                      onStepAction,
                                      RequiredDocuments,
                                      Documents = [],
                                      onFilesDrop,
                                      accept,
                                      onPreviewDocument,
                                      Versions = [],
                                      onVersionSelect,
                                      uploadingStepID = null,
                                      onArchiveAndUseLatestTemplate = () => null,
                                      canCreateChecklist,
                                      canToggleSubStep = true,
                                      isResetting = false,
                                  }) {
    const firstNonCompletedIdx = items.findIndex(item => item.StatusID !== STATUS_COMPLETED);

    const [isChecklistSidebarOpen, setIsChecklistSidebarOpen] = useState(false);
    const [selectedVersionIdx, setSelectedVersionIdx] = useState(0);
    const [isVersionLoading, setIsVersionLoading] = useState(false);

    const isArchivedVersion = selectedVersionIdx !== 0;

    useEffect(() => {
        if (!isLoading) {
            setIsVersionLoading(false);
        }
    }, [isLoading]);

    const isSkeletonVisible = (isLoading && items.length === 0) || isVersionLoading || isResetting;

    const total = items.length;
    const completed = items.filter(i => i.StatusID === STATUS_COMPLETED).length;
    const waiting = items.filter(i => i.StatusID === STATUS_WAITING).length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;


    return (
        <div className="relative">
            <SlideOver
                title={translate("dialog_heading.checklist_history")}
                open={isChecklistSidebarOpen}
                setOpen={(bool) => setIsChecklistSidebarOpen(bool)}
                widthClass="max-w-[300px]"
            >
                <div className="pr-2 pt-2 pb-20 space-y-2">
                    {Versions.map((date, idx) => {
                        const isSelected = idx === selectedVersionIdx;

                        const handleClick = () => {
                            setSelectedVersionIdx(idx);
                            setIsVersionLoading(true);
                            onVersionSelect?.(idx === 0 ? null : date);
                        };

                        return isSelected ? (
                            <div
                                key={idx}
                                className="w-72 border-l-2 border-emerald-500 bg-emerald-500/10 px-4 py-3 cursor-pointer rounded-r-md"
                                onClick={handleClick}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>

                                        <span className="text-sm font-medium text-tm-gray-800">
                                            {idx === 0 ? 'Current version' : `Version — ${toFrontDateFromUTC(date)}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-1.5 pl-4 space-y-0.5">
                                    <p className="text-xs text-gray-500">{toFrontDateTimeFromUTC(date)}</p>
                                </div>
                            </div>
                        ) : (
                            <div
                                key={idx}
                                className="w-72 border-l-2 border-transparent hover:border-gray-300 hover:bg-gray-50 px-4 py-3 cursor-pointer rounded-r-md transition-colors"
                                onClick={handleClick}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-gray-300 shrink-0"></span>

                                    <span className="text-sm font-medium text-gray-700">
                                        {idx === 0 ? 'Current version' : `Version — ${toFrontDateFromUTC(date)}`}
                                    </span>
                                </div>

                                <div className="mt-1.5 pl-4 space-y-0.5">
                                    <p className="text-xs text-gray-500">{toFrontDateTimeFromUTC(date)}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!!onArchiveAndUseLatestTemplate && (
                    <div className="sticky bottom-0 bg-inverse border-t border-tm-gray-300 p-4">
                        <Button
                            className="w-full flex items-center justify-center rounded-btn bg-primary hover:bg-primary-shade text-inverse text-sm font-medium px-4 py-2 transition"
                            onClick={() => {
                                onArchiveAndUseLatestTemplate();
                                setIsChecklistSidebarOpen(false);
                            }}
                            hasPerm={canCreateChecklist}
                        >
                            {notFound ? translate("btn.use_latest_template") : translate("btn.archive_and_use_latest_template")}
                        </Button>
                    </div>
                )}
            </SlideOver>

            {!isSkeletonVisible && (
                <ButtonIcon
                    onClick={() => setIsChecklistSidebarOpen(true)}
                    addClass="!absolute right-0 top-1"
                >
                    <ClockIcon className="size-6 text-primary"/>
                </ButtonIcon>
            )}

            {isSkeletonVisible && (
                <ButtonIcon
                    onClick={() => null}
                    addClass="!absolute right-0 top-1"
                >
                    <LoaderSmall disableContainer={true}/>
                </ButtonIcon>
            )}

            <div className="max-w-4xl w-full mx-auto pb-12">

                {notFound ? (
                    <p className="text-tm-gray-700 text-sm py-6 text-center max-w-lg mx-auto">No checklist template was
                        available during candidate creation. Create a checklist template in settings page <a
                            href="/personnel-settings" target="_blank" rel="noreferrer"
                            className="text-primary underline hover:text-primary-shade">here</a>, then press &quot;Use
                        latest template&quot; in the <button type="button"
                                                             onClick={() => setIsChecklistSidebarOpen(true)}
                                                             className="text-primary underline hover:text-primary-shade cursor-pointer">checklist
                            history sidebar</button>.</p>
                ) : (
                    <ChecklistProgress
                        title={title}
                        total={total}
                        isLoading={isSkeletonVisible}
                        completed={completed}
                        waiting={waiting}
                        progress={progress}
                    />
                )}

                {!notFound && !isSkeletonVisible && (
                    <div className="flex flex-col gap-2.5">
                        {(() => {
                            let blocked = false;

                            return items.map((item, idx) => {
                                const isBlocked = blocked;

                                if (item.CanSkipStep === 0 && item.StatusID !== STATUS_COMPLETED) {
                                    blocked = true;
                                }
                                return (
                                    <div
                                        key={item.ChecklistStepID ?? idx}
                                        className={classNames("bg-inverse rounded-2xl shadow-sm", item.StatusID === STATUS_WAITING && "border border-amber-400")}
                                    >
                                        <ChecklistItem
                                            item={item}
                                            actions={actions}
                                            defaultOpen={idx === firstNonCompletedIdx}
                                            translate={translate}
                                            isLoading={isLoading}
                                            isUploadLoading={uploadingStepID === item.ChecklistStepID}
                                            isBlocked={isBlocked}
                                            isReadOnly={isArchivedVersion}
                                            canToggleSubStep={canToggleSubStep}
                                            onSaveNote={(note) => onSaveNote(item, note)}
                                            RequiredDocuments={RequiredDocuments}
                                            Documents={Documents}
                                            onToggleSubStep={(subIdx, newCompleted, subStepID) => onToggleSubStep(item, subIdx, newCompleted, subStepID)}
                                            onStepAction={(stepID, statusID) => onStepAction(item, stepID, statusID)}
                                            onFilesDrop={onFilesDrop}
                                            accept={accept}
                                            onPreviewDocument={onPreviewDocument}
                                        />
                                    </div>
                                );
                            });
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
}
